import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from '../utils/toast';
import { Users, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlanDetailsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [planDetails, setPlanDetails] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/signin');
      return;
    }
    fetchPlanDetails();
  }, [navigate]);

  const fetchPlanDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscriptions/plan-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlanDetails(response.data);
    } catch (error) {
      toast.error('Failed to fetch plan details');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !planDetails) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const usagePercentage = (planDetails.active_staff / planDetails.total_staff_allowed) * 100;

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Plan Details</h1>
            <Button onClick={() => navigate('/subscription')} variant="outline">
              Back
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="p-8">
          <h2 className="text-[22px] font-bold mb-6" style={{ color: '#0056B3' }}>{planDetails.plan_name}</h2>

          {/* Usage Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <Users className="w-10 h-10 text-[#0056B3] mx-auto mb-2" />
              <p className="text-[28px] font-bold" style={{ color: '#0056B3' }}>
                {planDetails.total_staff_allowed === 999999 ? '∞' : planDetails.total_staff_allowed}
              </p>
              <p className="text-sm text-gray-600">Total Staff Allowed</p>
            </div>

            <div className="text-center p-6 bg-green-50 rounded-lg">
              <Users className="w-10 h-10 text-green-600 mx-auto mb-2" />
              <p className="text-[28px] font-bold text-green-600">{planDetails.active_staff}</p>
              <p className="text-sm text-gray-600">Active Staff</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <Users className="w-10 h-10 text-gray-600 mx-auto mb-2" />
              <p className="text-[28px] font-bold text-gray-600">{planDetails.remaining_licenses}</p>
              <p className="text-sm text-gray-600">Remaining Licenses</p>
            </div>
          </div>

          {/* Usage Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold">License Usage</h3>
              <span className="text-sm font-medium">{usagePercentage.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-6">
              <div
                className="bg-gradient-to-r from-[#0056B3] to-[#00AEEF] h-6 rounded-full flex items-center justify-center text-white text-xs font-medium transition-all duration-500"
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              >
                {usagePercentage > 10 && `${planDetails.active_staff} / ${planDetails.total_staff_allowed === 999999 ? '∞' : planDetails.total_staff_allowed}`}
              </div>
            </div>
          </div>

          {/* Concurrent Users */}
          <div className="mb-8 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <Users className="w-6 h-6 text-[#0056B3]" />
              <div>
                <p className="font-semibold">Concurrent Users Limit</p>
                <p className="text-sm text-gray-600">{planDetails.concurrent_users} users can access simultaneously</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-4">
            <Button
              onClick={() => navigate('/subscription/plans')}
              className="flex-1 bg-[#0056B3] hover:bg-[#0056B3]/90"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Upgrade Plan
            </Button>
            <Button
              onClick={() => navigate('/subscription/renew')}
              variant="outline"
              className="flex-1 border-[#0056B3] text-[#0056B3]"
            >
              Renew Subscription
            </Button>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default PlanDetailsPage;
